import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';

type Translate = (key: string) => string;

export const getCoverageStats = (t: Translate): FeatureGridItem[] => [
  { value: '600', suffix: 'M+', title: t('Company profiles, resolvable to a registry entry') },
  { value: '400', suffix: '+', title: t('Government registries read directly at source') },
  { value: '200', suffix: '+', title: t('Countries and territories, including thin-file markets') },
  { value: t('Daily'), suffix: '', title: t('Refresh cadence, with change events pushed to your systems') },
];

export const getFaqData = (t: Translate): FaqType[] => [
  {
    id: 'different-from-duns',
    question: t('How is this different from a DUNS-based approach?'),
    answer: t(
      'The identifier. A DUNS number is issued and licensed by a vendor, so it becomes a dependency the moment it spreads through your systems. We resolve to the registration number issued by the government registry — a public identifier you can use, publish, and reconcile against without a licence, and that stays valid whoever supplies your data.',
    ),
    default_opened: true,
  },
  {
    id: 'ambiguous-match',
    question: t('What happens when a match is ambiguous?'),
    answer: t(
      "It stays ambiguous rather than being forced. Records below your auto-merge threshold return the candidate set with the evidence for each — which attributes agreed and which were missing — and route to a steward queue. Adjudications are retained, so the same record isn't re-reviewed at the next refresh.",
    ),
  },
  {
    id: 'keep-internal-ids',
    question: t('Can we keep our own internal IDs?'),
    answer: t(
      'Yes, and most customers do. Your internal key stays primary; the registry identifier is added as the cross-system anchor that lets CRM, ERP, and billing records recognise each other. Nothing requires you to re-key your estate.',
    ),
  },
  {
    id: 'how-current',
    question: t('How current is the data?'),
    answer: t(
      'Registries are read on a daily cycle, and each field carries the timestamp of the read that produced it — so currency is a property of the record rather than a claim on a website. Publication cadence does vary by registry: some file continuously, others in batches, and the retrieval timestamp makes that visible per field.',
    ),
  },
  {
    id: 'redistribute-records',
    question: t('Can we redistribute enriched records in our own product?'),
    answer: t(
      'Reseller and redistribution rights are available and negotiated as part of the licence. If company data will surface to your own customers, raise it early and the terms can be scoped around that use.',
    ),
  },
  {
    id: 'thin-filings',
    question: t('What about companies with very thin filings?'),
    answer: t(
      "Identity, status, and address still resolve from the registry, because those are filed everywhere. Financial depth genuinely varies by jurisdiction — where figures aren't filed, estimated revenue and headcount are provided as modelled values and labelled as modelled, so a filed figure and an estimate are never mixed in the same field.",
    ),
  },
  {
    id: 'expected-match-rate',
    question: t('What match rate can we expect on our data?'),
    answer: t(
      "It depends on what you send, and we'd rather show you than quote a headline number. Records that arrive with a registration number, postcode, or domain resolve at very high rates automatically; a bare, misspelt name in a common-name market lands in the review band by design — forcing those to auto-merge is exactly how false merges happen. The number that matters is the split between auto-merge, review, and no-match, and we'll return that split on a sample of your own file before you commit.",
    ),
  },
  {
    id: 'same-name-different-countries',
    question: t('Two companies share a name in different countries — how do you tell them apart?'),
    answer: t(
      'Jurisdiction is part of the key. Because identity resolves to the registry a company is incorporated in, "Acme Ltd" at Companies House and "Acme LLC" in Delaware are never the same record — different registers, different registration numbers. When an input doesn\'t say which country it belongs to, the candidates are returned side by side for a steward rather than collapsed into a guess.',
    ),
  },
  {
    id: 'existing-mdm-platform',
    question: t('We already run an MDM platform — Informatica, Reltio. How does this fit?'),
    answer: t(
      'It feeds the hub rather than replacing it. We are the company-entity layer — resolution, registry identifiers, and enriched attributes with provenance — delivered through the API, batch, or your warehouse. Your platform keeps governing survivorship and stewardship; we supply the matched, sourced company data underneath it, so the golden record your teams already trust gets a public identifier and a traceable source on every field.',
    ),
  },
  {
    id: 'overwrite-records',
    question: t('Do you overwrite our records, or do we stay in control?'),
    answer: t(
      'You stay in control. Nothing is overwritten by default. You set the survivorship rules — which system wins for which field, and where the registry should override an internal value — and changes arrive as events you can accept, route to a steward, or ignore. The golden record is governed by your rules, not ours.',
    ),
  },
];
